import classNames from 'classnames';
import React, {Component, HTMLProps} from 'react';
import {CopyToClipboard} from 'react-copy-to-clipboard';
import {FieldRenderProps} from 'react-final-form';
import {Link} from 'react-router';
import * as config from '~/config';
import EyeIcon from './EyeIcon/EyeIcon';
import styles from './Form.module.scss';

const {CONTACT_SUPPORT_MESSAGE} = config;

interface InputTextProps {
    name?: string;
    type?: HTMLProps<HTMLInputElement>['type'];
    icon?: {
        type: 'copy' | string;
        action?: () => void;
    };
    max?: number;
    maxlength?: number;
    readOnly?: boolean;
    refName?: React.RefObject<HTMLInputElement>;
    css?: React.CSSProperties;
    autoFocus?: boolean;
    title?: string;
    hint?: string;
    hintCss?: string;
    input?: Omit<HTMLProps<HTMLInputElement>, 'type'>;
    errorHint?: () => void;
    meta?: {
        active?: boolean;
        error?: string | undefined;
        touched?: boolean;
    };
    apiError?: string | boolean;
    disabled?: boolean;
    breakingLine?: string;
    placeholder?: string;
    showEye?: boolean;
    step?: number | 'any';
}

interface InputTextState {
    passwordShown: boolean;
}

class InputText extends Component<InputTextProps, InputTextState> {
    constructor(props: InputTextProps) {
        super(props);
        this.state = {
            passwordShown: false,
        };
        this.togglePasswordVisibility = this.togglePasswordVisibility.bind(this);
    }

    togglePasswordVisibility = () => {
        this.setState({passwordShown: !this.state.passwordShown});
    };

    displayError() {
        const {apiError} = this.props;
        let linkToSupport = null;
        if (apiError && typeof apiError === 'string' && apiError?.includes(CONTACT_SUPPORT_MESSAGE)) {
            const [mainPart, ...rest] = apiError.split(`${CONTACT_SUPPORT_MESSAGE}`);
            linkToSupport = (
                <>
                    {mainPart}
                    <Link to='/support/form' className={styles.contactSupport}>
                        {CONTACT_SUPPORT_MESSAGE}
                    </Link>
                    {rest.join('')}
                </>
            );
        }
        return linkToSupport || apiError;
    }

    render() {
        const {
            name,
            type = 'text',
            icon,
            max,
            maxlength,
            readOnly,
            refName,
            css,
            autoFocus,
            title,
            hint = '',
            hintCss,
            input,
            errorHint,
            meta,
            apiError,
            disabled,
            breakingLine = 'false',
            placeholder,
            showEye,
        } = this.props;

        const step = this.props.step ? this.props.step : 'any';
        let hintClass = styles.hint;
        if ((meta?.touched && meta?.error) || apiError) {
            hintClass = styles.hint_error;
        } else if (hintCss) {
            hintClass = styles[hintCss as keyof typeof styles];
        }

        const rootClasses = classNames(styles.inputText, {
            [styles.inputText_error]: ((meta?.touched && meta?.error) || apiError) && type,
            [styles.inputText_active]: meta?.active,
            [styles.inputText_disabled]: disabled,
        });

        const inputClasses = classNames(
            {
                [styles.input_date]: type === 'date',
                [styles.input_password]: type === 'password',
            },
            styles.input
        );

        return (
            <div className={rootClasses} style={css} data-breaking-line={breakingLine}>
                <label htmlFor={`${name}-label`} className={styles.inputText__label}>
                    {title}
                </label>
                {icon?.type && icon.type === 'copy' && (
                    <CopyToClipboard text={input?.value as string}>
                        <i className={styles[`icon_${icon.type}`]} />
                    </CopyToClipboard>
                )}
                {icon && (!icon.type || icon.type !== 'copy') && icon.action && (
                    <i className={styles[`icon_${icon.type}` as keyof typeof styles]} onClick={icon.action} />
                )}
                {icon && (!icon.type || icon.type !== 'copy') && !icon.action && (
                    <i className={styles[`icon_${icon.type}` as keyof typeof styles]} />
                )}
                {showEye && (
                    <EyeIcon showEyeIconOff={this.state.passwordShown} onClickHandler={this.togglePasswordVisibility} />
                )}

                <input
                    autoFocus={autoFocus}
                    max={max}
                    ref={refName}
                    readOnly={readOnly}
                    {...input}
                    id={`${name}-label`}
                    className={inputClasses}
                    type={this.state.passwordShown ? 'text' : type}
                    step={step}
                    placeholder={placeholder}
                    maxLength={maxlength}
                    disabled={!!disabled}
                    onKeyDown={e => type === 'date' && e.preventDefault()}
                />
                <div className={hintClass}>
                    {meta?.touched && meta?.error ? meta?.error : apiError ? this.displayError() : hint}{' '}
                    {errorHint && apiError ? (
                        <span className={styles.errorHint} onClick={errorHint}>
                            ?
                        </span>
                    ) : (
                        ''
                    )}
                </div>
            </div>
        );
    }
}

export default InputText;

/**
 * @todo FIXME: this exists to stop ts from complaining.
 * Input and meta props in InputText can be defined FieldRenderProps but then changes to ForgotPassword & ChangePassword required
 */
export const FinalInputText = (props: FieldRenderProps<string> & Omit<InputTextProps, 'input' | 'meta'>) => (
    <InputText
        {...props}
        input={{
            ...props.input,
            onChange: ev => {
                props.onChange?.(ev);
                props.input.onChange(ev);
            },
        }}
    />
);
